import { useId } from 'react';
import { tokens } from '../../theme';
import type { AndDigitalLogoProps } from './types';

/**
 * The AND Digital mark, for the corner of the login page. Verbatim from Luna's
 * `and-logo.svg`, so the two login pages sign themselves identically.
 *
 * **The "AND" letters are a mask, not paths** — a red block with the letterforms
 * punched out, so whatever sits behind shows through. That is what makes
 * `knockedOut` possible: on a red ground the block goes white and the ground
 * fills the letters, where a red block would simply disappear.
 *
 * Inside a `<mask>`, white shows and black hides. The letterforms are given an
 * explicit black rather than relying on the default, because that default is
 * load-bearing.
 */

/** From the viewBox: 661.81 wide by 126.72 tall. */
const ASPECT_RATIO = 661.81 / 126.72;

/** The seven glyphs of "digital", in the order the source file has them. */
const DIGITAL_PATHS = [
  'M370.18,22.49c12.33,0,20.44,2.67,26.77,7.33c9.55,7.11,15.44,18.66,15.44,31.55s-5.89,24.44-15.44,31.55 c-6.33,4.67-14.44,7.33-26.77,7.33h-24.44V22.49H370.18L370.18,22.49z M391.73,44.48c-4.33-5.44-10.67-8.67-19.89-8.67h-11.11 v51.1h11.11c9.22,0,15.55-3.22,19.89-8.66c3.56-4.44,5.66-10.44,5.66-16.89C397.4,54.93,395.29,48.93,391.73,44.48z',
  'M502.15,97.25c0,9.67-1.67,14.14-4.89,18.69c-4.89,6.78-13.55,10.78-23.88,10.78 c-10.55,0-20.55-4.89-25.89-11.11l9.11-9.44c2.89,3.11,7.89,7.78,16.78,7.78c4.67,0,8.55-1.45,11-4.33 c2.22-2.45,3.33-5.78,3.33-12.67v-0.47c-4.33,3.33-9,4.89-14.44,4.89c-8,0-15.66-3.55-20.44-9.11 c-4.22-4.89-6.78-11.55-6.78-18.89c0-15.89,11.78-28.11,26.44-28.11c6.11,0,11.55,2,15.78,5.44v-4.33h13.88L502.15,97.25 L502.15,97.25z M487.71,65.26c-3-4-7.78-6.66-13.33-6.66c-8.44,0-13.89,7-13.89,14.78c0,8.44,6.11,14.66,14.22,14.66 c5.44,0,10.22-2.67,13-6.67V65.26z',
  'M522.04,21.31c4.89,0,8.84,3.74,8.84,8.63s-3.95,8.63-8.84,8.63s-8.84-3.74-8.84-8.63 S517.15,21.31,522.04,21.31z M529.26,100.25h-14.44V46.37h14.44V100.25z',
  'M429.28,21.31c4.89,0,8.84,3.74,8.84,8.63s-3.95,8.63-8.84,8.63s-8.84-3.74-8.84-8.63 S424.39,21.31,429.28,21.31z M436.5,100.25h-14.44V46.37h14.44V100.25z',
  'M573.59,100.25c-1.89,0.67-6.22,1.11-9,1.11c-8.66,0-15-2.89-18.66-7.78c-2.78-3.67-4-8.44-4-16.11V58.59 V46.37v-15h14.44v15h15.08v12.22h-15.08v20.55c0,3.33,0.55,5.33,1.78,6.78c1.44,1.67,3.78,2.66,7.33,2.66 c2.11,0,4.89-0.33,5.89-0.56L573.59,100.25z',
  'M634.69,100.25H620.8v-4.33c-4.44,3.78-9.55,5.44-15,5.44c-8,0-15.55-3.55-20.33-9.11 c-4.22-4.89-6.89-11.56-6.89-18.89c0-15.89,11.77-28.11,26.44-28.11c6.11,0,11.55,2,15.77,5.44v-4.33h13.89v53.89H634.69z M620.25,65.26c-3-4-7.78-6.66-13.33-6.66c-8.44,0-13.89,7-13.89,14.78c0,8.44,6.11,14.66,14.22,14.66c5.44,0,10.22-2.67,13-6.66 L620.25,65.26L620.25,65.26z',
  'M661.81,100.25h-14.45V22.49h14.44L661.81,100.25z',
];

/** The A, N and D letterforms, punched out of the block above. */
const AND_LETTER_PATHS = [
  'M261.88,22.49h-38.89v18.83h9.92v40.11h-9.92v18.83h38.89c21.44,0,38.88-17.44,38.88-38.88 C300.76,39.93,283.32,22.49,261.88,22.49z M261.88,81.42h-10.14V41.31h10.14c11.06,0,20.05,9,20.05,20.05 C281.94,72.43,272.94,81.42,261.88,81.42z',
  'M61.38,22.49c-21.44,0-38.88,17.44-38.88,38.88v38.89h18.83v-9.92h40.11v9.92h18.83V61.37 C100.26,39.93,82.82,22.49,61.38,22.49z M81.43,71.5H41.32V61.36c0-11.06,9-20.05,20.05-20.05c11.06,0,20.05,8.99,20.05,20.05 L81.43,71.5L81.43,71.5z',
];

const N_POINTS =
  '200.51,100.25 181.68,100.25 141.57,53.2 141.57,100.25 122.75,100.25 122.75,22.49 141.57,22.49 181.68,69.49 181.68,22.49 200.51,22.49';

export const AndDigitalLogo = ({ height = 24, knockedOut = false }: AndDigitalLogoProps) => {
  // **The mask id has to be unique per instance**: `url(#id)` resolves
  // document-wide, so two of these on a page would share the first one's mask.
  // `useId`'s colons and guillemets are stripped — a URL fragment is fussier
  // than an HTML id attribute.
  const maskId = `and-text-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <svg
      width={Math.round(height * ASPECT_RATIO)}
      height={height}
      viewBox="0 0 661.81 126.72"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="AND Digital"
    >
      <mask id={maskId}>
        {/* White keeps, black cuts. */}
        <rect width="100%" height="100%" fill="white" />
        <polygon points={N_POINTS} fill="black" />
        {AND_LETTER_PATHS.map((d) => (
          <path key={d} d={d} fill="black" />
        ))}
      </mask>

      {/*
        Behind the block. White on a non-red ground so the letters read white;
        absent when knocked out, which is what lets the page colour through.
      */}
      <rect
        y="0"
        width="323.25"
        height="122.74"
        fill={knockedOut ? 'none' : tokens.text.onBrand}
      />
      <rect
        y="0"
        width="323.25"
        height="122.74"
        fill={knockedOut ? tokens.text.onBrand : tokens.action.primary}
        mask={`url(#${maskId})`}
      />

      {/* "digital", always white — it only ever sits on a coloured ground here. */}
      {DIGITAL_PATHS.map((d) => (
        <path key={d} d={d} fill={tokens.text.onBrand} />
      ))}
    </svg>
  );
};
